// Types for API responses
export interface Setting {
    key: string;
    value: string;
    type: 'text' | 'rich_text' | 'json' | 'boolean';
    updated_by: string;
    updated_at: string;
    version: number;
}

export interface ProcessingParameters {
    thumbnail_crop?: {
        x: number;
        y: number;
        width: number;
        height: number;
    };
    full_image_crop?: {
        x: number;
        y: number;
        width: number;
        height: number;
    };
    button_crop?: {
        x: number;
        y: number;
        width: number;
        height: number;
    };
    watermark_settings?: {
        full_image: 'white' | 'black' | 'none';
        thumbnail: 'white' | 'black' | 'none';
        opacity: number;
    };
    display_dimensions?: {
        width: number;
        height: number;
    };
}

export interface GalleryItem {
    id: string;
    title: string;
    description?: string;
    thumbnail_url: string;
    full_image_url: string;
    order: number;
    is_public: boolean;
    created_at: string;
    updated_at: string;
    created_by: string;
    updated_by: string;
    original_image_url?: string;
    processing_parameters?: ProcessingParameters;
    item_type?: 'gallery' | 'button';
    button_text?: string;
}

export interface CreateGalleryItemRequest {
    title: string;
    description?: string;
    thumbnail_url: string;
    full_image_url: string;
    is_public?: boolean;
    order?: number;
    original_image_url?: string;
    processing_parameters?: ProcessingParameters;
    item_type?: 'gallery' | 'button';
    button_text?: string;
}

export interface PresignedUrlResponse {
    presignedUrl: string;
    fileKey: string;
    bucketName: string;
    cloudFrontUrl?: string;
    expiresIn: number;
}

export interface PresignedUrlRequest {
    fileName: string;
    fileType: string;
    bucketType: 'thumbnails' | 'images' | 'originals';
}


export interface UserProfile {
    id: string;
    email: string;
    full_name: string;
    admin_role: string;
    created_at: string;
    updated_at: string;
    last_login?: string;
    is_active: boolean;
}

export interface ApiError {
    message: string;
    status?: number;
    details?: any;
}

class ApiService {
    private getApiBaseUrl(): string {
        // Check if we're in a browser environment
        if (typeof window !== 'undefined') {
            const hostname = window.location.hostname;
            
            // Local development - use relative URLs (Vite proxy handles this)
            if (hostname === 'localhost' || hostname === '127.0.0.1') {
                return '/api';
            }
            
            // Beta environment - use direct API Gateway URL
            if (hostname === 'beta.beadsbyjill.com') {
                return 'https://1ghid62w5j.execute-api.us-east-1.amazonaws.com/beta';
            }
            
            // Production environment - use direct API Gateway URL
            if (hostname === 'beadsbyjill.com' || hostname === 'www.beadsbyjill.com') {
                return 'https://hv9t8x63rk.execute-api.us-east-1.amazonaws.com/prod';
            }
        }
        
        // Fallback to beta for unknown environments
        return 'https://1ghid62w5j.execute-api.us-east-1.amazonaws.com/beta';
    }

    private getCachedApiBaseUrl(): string {
        // Check if we're in a browser environment
        if (typeof window !== 'undefined') {
            const hostname = window.location.hostname;
            
            // Local development - use relative URLs (Vite proxy handles this)
            if (hostname === 'localhost' || hostname === '127.0.0.1') {
                return '/api';
            }
            
            // Beta environment - use CloudFront URL for cached API
            if (hostname === 'beta.beadsbyjill.com') {
                // TODO: Replace with actual CloudFront distribution URL after deployment
                return 'https://1ghid62w5j.execute-api.us-east-1.amazonaws.com/beta';
            }
            
            // Production environment - use CloudFront URL for cached API
            if (hostname === 'beadsbyjill.com' || hostname === 'www.beadsbyjill.com') {
                // TODO: Replace with actual CloudFront distribution URL after deployment
                return 'https://hv9t8x63rk.execute-api.us-east-1.amazonaws.com/prod';
            }
        }
        
        // Fallback to beta for unknown environments
        return 'https://1ghid62w5j.execute-api.us-east-1.amazonaws.com/beta';
    }


    private async getAuthHeaders(): Promise<Record<string, string>> {
        try {
            const { fetchAuthSession } = await import('aws-amplify/auth');
            const session = await fetchAuthSession();
            return {
                'Authorization': `Bearer ${session.tokens?.idToken?.toString() || ''}`,
                'Content-Type': 'application/json',
            };
        } catch (error) {
            console.warn('Failed to get auth session:', error);
            return {
                'Content-Type': 'application/json',
            };
        }
    }

    private async makeRequest<T>(
        endpoint: string,
        options: RequestInit = {},
        useCachedUrl: boolean = false
    ): Promise<T> {
        // Build the full URL based on environment
        const baseUrl = useCachedUrl ? this.getCachedApiBaseUrl() : this.getApiBaseUrl();
        const url = baseUrl.startsWith('http') ? `${baseUrl}${endpoint}` : `${baseUrl}${endpoint}`;
        const headers = await this.getAuthHeaders();

        const config: RequestInit = {
            ...options,
            headers: {
                ...headers,
                ...options.headers,
            },
        };

        try {
            const response = await fetch(url, config);
            
            if (!response.ok) {
                let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.error || errorMessage;
                } catch {
                    // If we can't parse the error response, use the default message
                }
                
                throw new Error(errorMessage);
            }

            // Parse JSON response directly
            const data = await response.json();
            return data;
        } catch (error) {
            console.error(`API request failed for ${endpoint}:`, error);
            throw error;
        }
    }

    // Settings Operations
    async getSettings(): Promise<{ settings: Setting[] }> {
        return this.makeRequest<{ settings: Setting[] }>('/admin/settings', { method: 'GET' });
    }

    async getPublicSettings(): Promise<{ settings: { key: string; value: string }[] }> {
        return this.makeRequest<{ settings: { key: string; value: string }[] }>('/public/settings', { method: 'GET' }, true);
    }

    async getPublicAbout(): Promise<{ aboutContent: string }> {
        return this.makeRequest<{ aboutContent: string }>('/public/about', { method: 'GET' }, true);
    }

    async trackExternalClick(linkType: 'etsy' | 'instagram'): Promise<void> {
        // Fire and forget - use keepalive and don't await
        const baseUrl = this.getApiBaseUrl();
        const url = baseUrl.startsWith('http') ? `${baseUrl}/public/track-click` : `${baseUrl}/public/track-click`;
        
        // Use fetch with keepalive for fire-and-forget behavior
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ linkType }),
            keepalive: true,
        }).catch(error => {
            // Silently fail - we don't want to break user experience
            console.warn('Failed to track external link click:', error);
        });
    }

    async getSetting(key: string): Promise<{ setting: Setting }> {
        return this.makeRequest<{ setting: Setting }>(`/admin/settings/${encodeURIComponent(key)}`, { method: 'GET' });
    }

    async updateSetting(key: string, value: string, type: string): Promise<{ setting: Setting; message: string }> {
        return this.makeRequest<{ setting: Setting; message: string }>(`/admin/settings/${encodeURIComponent(key)}`, {
            method: 'PUT',
            body: JSON.stringify({ value, type }),
        });
    }

    // Gallery Operations
    async getGalleryItems(): Promise<{ items: GalleryItem[] }> {
        return this.makeRequest<{ items: GalleryItem[] }>('/public/gallery', { method: 'GET' }, true);
    }

    async getPublicGalleryItem(id: string): Promise<{ item: GalleryItem }> {
        return this.makeRequest<{ item: GalleryItem }>(`/public/gallery/${id}`, { method: 'GET' }, true);
    }

    async getAllGalleryItems(): Promise<{ items: GalleryItem[] }> {
        console.log('🌐 Calling API endpoint: /admin/gallery/all');
        const baseUrl = this.getApiBaseUrl();
        console.log('🏠 Base URL:', baseUrl);
        console.log('🔗 Full URL will be:', `${baseUrl}/admin/gallery/all`);
        return this.makeRequest<{ items: GalleryItem[] }>('/admin/gallery/all', { method: 'GET' });
    }

    async getGalleryItem(id: string): Promise<{ item: GalleryItem }> {
        return this.makeRequest<{ item: GalleryItem }>(`/admin/gallery/${id}`, { method: 'GET' });
    }

    async createGalleryItem(item: CreateGalleryItemRequest): Promise<{ item: GalleryItem; message: string }> {
        return this.makeRequest<{ item: GalleryItem; message: string }>('/admin/gallery', {
            method: 'POST',
            body: JSON.stringify(item),
        });
    }

    async updateGalleryItem(id: string, updates: Partial<GalleryItem>): Promise<{ message: string; itemId: string }> {
        return this.makeRequest<{ message: string; itemId: string }>(`/admin/gallery/${id}`, {
            method: 'PUT',
            body: JSON.stringify(updates),
        });
    }

    async deleteGalleryItem(id: string): Promise<{ message: string; itemId: string }> {
        return this.makeRequest<{ message: string; itemId: string }>(`/admin/gallery/${id}`, {
            method: 'DELETE',
        });
    }

    // Upload Operations
    async generatePresignedUrl(request: PresignedUrlRequest): Promise<PresignedUrlResponse> {
        return this.makeRequest<PresignedUrlResponse>('/admin/upload/presigned-url', {
            method: 'POST',
            body: JSON.stringify(request),
        });
    }


    // User Operations
    async getUsers(): Promise<{ users: UserProfile[] }> {
        return this.makeRequest<{ users: UserProfile[] }>('/admin/users', { method: 'GET' });
    }

    async getUserProfile(userId: string): Promise<{ user: UserProfile }> {
        return this.makeRequest<{ user: UserProfile }>(`/admin/users/${userId}`, { method: 'GET' });
    }

    async createUser(userData: {
        email: string;
        fullName: string;
        adminRole: 'admin' | 'editor' | 'viewer';
    }): Promise<{ message: string; user: UserProfile }> {
        return this.makeRequest<{ message: string; user: UserProfile }>('/admin/users', {
            method: 'POST',
            body: JSON.stringify(userData),
        });
    }

    // Utility method for uploading files to S3
    async uploadFileToS3(
        file: File,
        bucketType: 'thumbnails' | 'images' | 'originals'
    ): Promise<{ fileKey: string; bucketName: string; cloudFrontUrl?: string }> {
        try {
            // Generate presigned URL
            const presignedResponse = await this.generatePresignedUrl({
                fileName: file.name,
                fileType: file.type,
                bucketType,
            });

            // Upload file to S3 using presigned URL
            const uploadResponse = await fetch(presignedResponse.presignedUrl, {
                method: 'PUT',
                body: file,
                headers: {
                    'Content-Type': file.type,
                },
            });

            if (!uploadResponse.ok) {
                throw new Error(`Failed to upload file: ${uploadResponse.statusText}`);
            }

            return {
                fileKey: presignedResponse.fileKey,
                bucketName: presignedResponse.bucketName,
                cloudFrontUrl: presignedResponse.cloudFrontUrl,
            };
        } catch (error) {
            console.error('File upload failed:', error);
            throw new Error(`File upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    // Utility method for uploading both thumbnail and full image
    async uploadGalleryImages(
        thumbnailFile: File,
        fullImageFile: File
    ): Promise<{ thumbnailUrl: string; fullImageUrl: string }> {
        try {
            const [thumbnailResult, fullImageResult] = await Promise.all([
                this.uploadFileToS3(thumbnailFile, 'thumbnails'),
                this.uploadFileToS3(fullImageFile, 'images'),
            ]);

            // Use CloudFront URLs from backend response to avoid CORS issues
            const thumbnailUrl = thumbnailResult.cloudFrontUrl || `https://${thumbnailResult.bucketName}.s3.us-east-1.amazonaws.com/${thumbnailResult.fileKey}`;
            const fullImageUrl = fullImageResult.cloudFrontUrl || `https://${fullImageResult.bucketName}.s3.us-east-1.amazonaws.com/${fullImageResult.fileKey}`;

            return {
                thumbnailUrl,
                fullImageUrl,
            };
        } catch (error) {
            console.error('Gallery image upload failed:', error);
            throw new Error(`Gallery image upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    // Utility method for uploading original image
    async uploadOriginalImage(originalFile: File): Promise<{ fileKey: string; bucketName: string; originalImageUrl: string }> {
        try {
            const result = await this.uploadFileToS3(originalFile, 'originals');
            
            // Use CloudFront URL from backend response to avoid CORS issues
            const originalImageUrl = result.cloudFrontUrl || `https://${result.bucketName}.s3.us-east-1.amazonaws.com/${result.fileKey}`;
            
            return {
                fileKey: result.fileKey,
                bucketName: result.bucketName,
                originalImageUrl,
            };
        } catch (error) {
            console.error('Original image upload failed:', error);
            throw new Error(`Original image upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }


}

// Export a singleton instance
export const apiService = new ApiService();

// Export the class for testing purposes
export { ApiService };
